import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Camera, Search, Trash2, ArrowDownToLine, ArrowUpFromLine, Star } from "lucide-react";
import MaterialeQRScanner from "@/components/MaterialeQRScanner";

// Il materiale lo carica un mezzo che lo porta in giro: furgoni attrezzati,
// autocarri, escavatori. Gli altri - saldatrici, pestelli, tagliasfalto - in
// quell'elenco sono solo rumore, e con oltre cento macchine in anagrafica
// trovare il proprio furgone diventa una caccia. Restano raggiungibili con
// "Tutti", perche' prima o poi il caso strano arriva.
const CATEGORIE_MEZZI = ["FURGONI ATTREZZATI", "AUTOCARRI", "ESCAVATORI"];

/**
 * Il magazzino che si muove: quello che entra e quello che esce.
 *
 * E' pensata per essere usata in piedi davanti allo scaffale, col telefono in
 * una mano: si sceglie una volta dove va la roba, poi si inquadra un'etichetta
 * dopo l'altra e si scrive quanto se ne prende. Chi preleva e' chi ha fatto
 * l'accesso, quindi non c'e' niente da firmare.
 */
export default function MovimentiMateriali() {
  const [params, setParams] = useSearchParams();
  const [tipo, setTipo] = useState("scarico");
  const [materiali, setMateriali] = useState([]);
  const [cantieri, setCantieri] = useState([]);
  const [mezzi, setMezzi] = useState([]);
  const [righe, setRighe] = useState([]);
  const [destinazioneTipo, setDestinazioneTipo] = useState("cantiere");
  const [destinazioneId, setDestinazioneId] = useState("");
  const [filtroMezzo, setFiltroMezzo] = useState("");   // "" = i tre soliti, "*" = tutti
  const [fornitore, setFornitore] = useState("");
  const [ddt, setDdt] = useState("");
  const [note, setNote] = useState("");
  const [cerca, setCerca] = useState("");
  const [scanner, setScanner] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const carica = useCallback(async () => {
    const [{ data: m }, { data: c }, { data: e }] = await Promise.all([
      api.get("/materials"),
      api.get("/sites"),
      api.get("/equipment"),
    ]);
    setMateriali(m);
    setCantieri(c.filter(s => s.status !== "chiuso"));
    setMezzi(e);
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const mezziFiltrati = useMemo(() => {
    const base = filtroMezzo === "*"
      ? mezzi
      : filtroMezzo
        ? mezzi.filter(m => (m.category || "") === filtroMezzo)
        : mezzi.filter(m => CATEGORIE_MEZZI.includes(m.category || ""));
    return [...base].sort((a, b) => (a.identifier || "").localeCompare(b.identifier || ""));
  }, [mezzi, filtroMezzo]);

  // Cambiando filtro, un mezzo scelto e ora nascosto non deve restare
  // selezionato di nascosto: sparirebbe dalla tendina restando nella bolla.
  useEffect(() => {
    if (destinazioneTipo !== "mezzo" || !destinazioneId) return;
    if (!mezziFiltrati.some(m => m.id === destinazioneId)) setDestinazioneId("");
  }, [mezziFiltrati, destinazioneId, destinazioneTipo]);

  const aggiungi = useCallback((mat) => {
    if (!mat) return;
    setRighe(prev => prev.some(r => r.material_id === mat.id)
      ? prev
      : [...prev, { material_id: mat.id, name: mat.name, unit: mat.unit,
                    stock: mat.stock, quantita: "1" }]);
    setCerca("");
  }, []);

  // L'etichetta inquadrata dalla fotocamera del telefono apre la pagina
  // pubblica dell'articolo, che rimanda qui col codice gia' in mano.
  useEffect(() => {
    const mat = params.get("mat");
    if (!mat || materiali.length === 0) return;
    // Dal QR si arriva anche con il verso gia' deciso: prelievo o carico.
    const verso = params.get("tipo");
    if (verso === "carico" || verso === "scarico") setTipo(verso);
    const trovato = materiali.find(m => m.id === mat);
    if (trovato) aggiungi(trovato);
    else toast.error("Articolo non trovato");
    params.delete("mat");
    params.delete("tipo");
    setParams(params, { replace: true });
  }, [params, setParams, materiali, aggiungi]);

  const daScansione = useCallback((matId) => {
    setScanner(false);
    const trovato = materiali.find(m => m.id === matId);
    if (trovato) { aggiungi(trovato); toast.success(trovato.name); }
    else toast.error("Questa etichetta non corrisponde a nessun articolo");
  }, [materiali, aggiungi]);

  const cambiaQuantita = (matId, valore) =>
    setRighe(prev => prev.map(r => r.material_id === matId ? { ...r, quantita: valore } : r));

  const togli = (matId) => setRighe(prev => prev.filter(r => r.material_id !== matId));

  const risultati = cerca.trim().length < 2 ? []
    : materiali.filter(m => m.name.toLowerCase().includes(cerca.toLowerCase())).slice(0, 8);

  const salva = async () => {
    if (righe.length === 0) return toast.error("Aggiungi almeno un articolo");
    if (tipo === "scarico" && !destinazioneId)
      return toast.error("Scegli dove va il materiale");
    const preparate = righe.map(r => ({
      material_id: r.material_id, quantita: parseFloat(String(r.quantita).replace(",", ".")),
    }));
    if (preparate.some(r => !(r.quantita > 0)))
      return toast.error("Controlla le quantità: devono essere maggiori di zero");

    setSalvando(true);
    try {
      const { data } = await api.post("/materials/movimenti", {
        tipo,
        righe: preparate,
        destinazione_tipo: tipo === "scarico" ? destinazioneTipo : null,
        destinazione_id: tipo === "scarico" ? destinazioneId : null,
        fornitore: tipo === "carico" ? (fornitore || null) : null,
        ddt: tipo === "carico" ? (ddt || null) : null,
        note: note || null,
      });
      toast.success(tipo === "carico"
        ? `Caricati ${data.righe} articoli`
        : `Prelevati ${data.righe} articoli`);
      setRighe([]); setNote(""); setDdt("");
      carica();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Non sono riuscito a salvare il movimento");
    } finally { setSalvando(false); }
  };

  const bottoneTipo = (valore, testo, Icona) => (
    <button type="button" onClick={() => setTipo(valore)} data-testid={`tipo-${valore}`}
            className={`flex-1 h-14 rounded-lg border flex items-center justify-center gap-2 font-semibold transition-colors ${
              tipo === valore ? "bg-orange-500 border-orange-500 text-white"
                              : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
      <Icona className="w-5 h-5" /> {testo}
    </button>
  );

  return (
    <div className="space-y-5 max-w-3xl" data-testid="movimenti-materiali">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-orange-600 mb-2">Materiali</div>
        <h1 className="font-display text-4xl font-black tracking-tighter">Carichi e prelievi</h1>
        <p className="text-sm text-slate-500 mt-1">
          Quello che entra a magazzino e quello che esce per un cantiere o un mezzo
        </p>
      </div>

      <div className="flex gap-2">
        {bottoneTipo("scarico", "Prelievo", ArrowUpFromLine)}
        {bottoneTipo("carico", "Carico", ArrowDownToLine)}
      </div>

      {tipo === "scarico" ? (
        <Card className="border-slate-200">
          <CardContent className="p-4 space-y-3">
            <Label>Dove va il materiale</Label>
            <div className="flex gap-2">
              {[["cantiere", "Cantiere"], ["mezzo", "Mezzo"]].map(([v, t]) => (
                <button key={v} type="button" data-testid={`destinazione-${v}`}
                        onClick={() => { setDestinazioneTipo(v); setDestinazioneId(""); }}
                        className={`text-sm px-4 py-2 rounded-full border transition-colors ${
                          destinazioneTipo === v ? "bg-slate-900 border-slate-900 text-white"
                                                 : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                  {t}
                </button>
              ))}
            </div>
            {destinazioneTipo === "mezzo" && (
              <div className="space-y-1">
                <div className="flex gap-1.5 flex-wrap">
                  {[["", "Quelli che caricano"], ...CATEGORIE_MEZZI.map(c => [c, c]), ["*", "Tutti"]]
                    .map(([v, t]) => (
                      <button key={v || "soliti"} type="button"
                              data-testid={`filtro-mezzo-${v || "soliti"}`}
                              onClick={() => setFiltroMezzo(v)}
                              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                filtroMezzo === v ? "bg-orange-500 border-orange-500 text-white"
                                                  : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                        {t}
                      </button>
                    ))}
                </div>
                <p className="text-[11px] text-slate-500">
                  {mezziFiltrati.length} mezzi fra cui scegliere
                </p>
              </div>
            )}
            <select className="h-11 border border-slate-200 rounded px-3 text-sm w-full"
                    value={destinazioneId} onChange={(e) => setDestinazioneId(e.target.value)}
                    data-testid="scelta-destinazione">
              <option value="">
                {destinazioneTipo === "cantiere" ? "Scegli il cantiere…" : "Scegli il mezzo…"}
              </option>
              {destinazioneTipo === "cantiere"
                ? cantieri.map(c => (
                    <option key={c.id} value={c.id}>
                      {c.name}{c.client ? ` — ${c.client}` : ""}
                    </option>))
                : mezziFiltrati.map(m => (
                    <option key={m.id} value={m.id}>
                      {m.identifier}{m.brand ? ` — ${m.brand} ${m.model || ""}` : ""}
                    </option>))}
            </select>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Fornitore</Label>
              <Input value={fornitore} onChange={(e) => setFornitore(e.target.value)}
                     placeholder="Chi ha consegnato" data-testid="campo-fornitore" />
            </div>
            <div>
              <Label>Numero bolla</Label>
              <Input value={ddt} onChange={(e) => setDdt(e.target.value)}
                     placeholder="DDT" data-testid="campo-ddt" />
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Cerca l'articolo per nome..." className="pl-9 h-12"
                 value={cerca} onChange={(e) => setCerca(e.target.value)}
                 data-testid="cerca-articolo" />
        </div>
        <Button variant="outline" className="h-12 px-4" onClick={() => setScanner(true)}
                data-testid="apri-scanner">
          <Camera className="w-5 h-5" />
        </Button>
      </div>

      {risultati.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            {risultati.map(m => (
              <button key={m.id} type="button" onClick={() => aggiungi(m)}
                      data-testid={`aggiungi-${m.id}`}
                      className="w-full text-left px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 flex items-center gap-2">
                {m.favorite && <Star className="w-3.5 h-3.5 fill-orange-400 text-orange-400 flex-shrink-0" />}
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium truncate">{m.name}</span>
                  <span className="block text-[11px] text-slate-500">{m.category}</span>
                </span>
                <span className="text-xs text-slate-400 flex-shrink-0">
                  {m.stock} {m.unit}
                </span>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {righe.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">
          Inquadra l'etichetta sullo scaffale, oppure cerca l'articolo per nome.
        </p>
      ) : (
        <div className="space-y-2">
          {righe.map(r => (
            <Card key={r.material_id} className="border-slate-200"
                  data-testid={`riga-${r.material_id}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold leading-tight">{r.name}</div>
                  <div className="text-[11px] text-slate-500">
                    a magazzino ce ne sono {r.stock} {r.unit}
                  </div>
                </div>
                <Input type="number" inputMode="decimal" step="0.01" min="0"
                       className="w-24 h-11 text-right" value={r.quantita}
                       data-testid={`quantita-${r.material_id}`}
                       onChange={(e) => cambiaQuantita(r.material_id, e.target.value)} />
                <span className="text-xs text-slate-400 w-8">{r.unit}</span>
                <button type="button" onClick={() => togli(r.material_id)}
                        className="text-slate-300 hover:text-red-600" title="Togli dalla lista">
                  <Trash2 className="w-4 h-4" />
                </button>
              </CardContent>
            </Card>
          ))}

          <div>
            <Label>Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)}
                   placeholder="Facoltative" data-testid="campo-note" />
          </div>

          <Button onClick={salva} disabled={salvando}
                  className="w-full h-14 bg-orange-500 hover:bg-orange-600 text-white text-base"
                  data-testid="salva-movimento">
            {salvando ? "Salvataggio…"
              : tipo === "carico" ? `Carica ${righe.length} articoli`
              : `Preleva ${righe.length} articoli`}
          </Button>
        </div>
      )}

      {scanner && (
        <MaterialeQRScanner onClose={() => setScanner(false)} onScanned={daScansione} />
      )}
    </div>
  );
}
